import type { GGCoinClient } from "@developerz.ai/ggcoin-sdk";
import {
  type Accessor,
  createContext,
  createEffect,
  createSignal,
  onCleanup,
  type ParentProps,
  useContext,
} from "solid-js";
import { errorReporting } from "../instrument.ts";
import { buildClient } from "../sdk/client.ts";
import { type PersistedSession, WalletStore } from "../sdk/storage.ts";

/** The master wallet, resolved once and shared so tab switches don't re-fetch. */
export interface MasterWalletInfo {
  id: string;
  status: "active" | "frozen";
}

interface AuthContextValue {
  client: GGCoinClient;
  /** Reactive session accessor, or `null` when signed out. */
  session: Accessor<PersistedSession | null>;
  /** Local cache of wallet ids the user has interacted with. */
  store: WalletStore;
  /** Reactive master-wallet accessor, or null until first `ensureMaster()`. */
  masterWallet: Accessor<MasterWalletInfo | null>;
  /** Resolve the master wallet once and cache it (deduped); instant on later calls. */
  ensureMaster(): Promise<MasterWalletInfo>;
  /** Update the cached master status after a freeze/unfreeze. */
  setMasterStatus(status: MasterWalletInfo["status"]): void;
  setSession(next: PersistedSession): void;
  clearSession(): void;
}

const AuthContext = createContext<AuthContextValue>();

/** Hydrate the SDK client + session on first mount; signals keep the UI in step. */
export function AuthProvider(props: ParentProps) {
  const store = new WalletStore();
  const [session, setSessionState] = createSignal<PersistedSession | null>(store.readSession());
  const client = buildClient(session()?.jwt ?? null);
  const [masterWallet, setMasterWallet] = createSignal<MasterWalletInfo | null>(null);
  // In-flight ensure call, so concurrent first-mounts share one request.
  let ensureInflight: Promise<MasterWalletInfo> | null = null;
  // Bumped on logout: an ensure() resolving after a session change is discarded.
  let sessionEpoch = 0;

  const setSession = (next: PersistedSession) => {
    store.writeSession(next);
    client.setToken(next.jwt);
    setSessionState(next);
  };

  const clearSession = () => {
    sessionEpoch += 1;
    store.clearSession();
    client.setToken(null);
    setSessionState(null);
    setMasterWallet(null);
    ensureInflight = null;
  };

  const ensureMaster = async (): Promise<MasterWalletInfo> => {
    const cached = masterWallet();
    if (cached) return cached;
    if (ensureInflight) return ensureInflight;
    const epoch = sessionEpoch;
    const pending: Promise<MasterWalletInfo> = client
      .ensureMasterWallet()
      .then((res) => {
        const info: MasterWalletInfo = { id: res.id, status: res.status };
        // Drop the result if the session changed (logout) while in-flight.
        if (epoch === sessionEpoch) {
          store.setPrimaryWallet(res.id);
          setMasterWallet(info);
        }
        if (ensureInflight === pending) ensureInflight = null;
        return info;
      })
      .catch((err) => {
        if (ensureInflight === pending) ensureInflight = null;
        throw err;
      });
    ensureInflight = pending;
    return pending;
  };

  const setMasterStatus = (status: MasterWalletInfo["status"]) => {
    setMasterWallet((prev) => (prev ? { ...prev, status } : prev));
  };

  // Error reporting starts only once a session exists; see instrument.ts.
  createEffect(() => {
    const jwt = session()?.jwt;
    if (jwt) void errorReporting.start(jwt);
  });

  // Live wallet-status push (#153): while signed in, subscribe to the per-account
  // wallet channel and keep the cached master status in step with a server-side
  // freeze/unfreeze (e.g. an operator action) — no manual refresh. App-level so
  // any page reading `masterWallet().status` (Dashboard badge, Send gating)
  // updates live regardless of which view is mounted.
  createEffect(() => {
    if (!session()) return;
    let unsub: (() => void) | null = null;
    let stale = false;
    client
      .subscribeWallet({
        onEvent: (event) => {
          if (event.type !== "wallet.status-changed") return;
          const master = masterWallet();
          if (master && event.walletId === master.id) setMasterStatus(event.status);
        },
        onError: () => {}, // transient — a reconnect/next event recovers
      })
      .then((u) => {
        if (stale) u();
        else unsub = u;
      })
      .catch(() => {});
    onCleanup(() => {
      stale = true;
      unsub?.();
    });
  });

  return (
    <AuthContext.Provider
      value={{
        client,
        session,
        store,
        masterWallet,
        ensureMaster,
        setMasterStatus,
        setSession,
        clearSession,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
}

/** Read the live auth context. Throws if mounted outside an `AuthProvider`. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
